import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Phone, Trash2, CheckCircle, Clock, Loader2, Reply, Send } from 'lucide-react';import { toast } from 'sonner';
import { contactAPI } from '../../utils/api';

const STATUS_FILTERS = [
  { id: '',            label: 'All' },
  { id: 'new',         label: 'New' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'resolved',    label: 'Resolved' },
];

const STATUS_BADGE = {
  new:         { label: 'New',         cls: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  resolved:    { label: 'Resolved',    cls: 'bg-green-100 text-green-700' },
};

const AdminContactTab = () => {
  const [status, setStatus] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await contactAPI.adminList(status || undefined);
      setMessages(r.data || []);
    } catch (_e) {
      toast.error('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = useCallback(async (id, next) => {
    try {
      await contactAPI.adminUpdateStatus(id, next);
      setMessages((prev) => prev.map((m) => m.message_id === id ? { ...m, status: next } : m));
      toast.success(`Marked ${STATUS_BADGE[next].label.toLowerCase()}`);
    } catch (_e) {
      toast.error('Failed to update status');
    }
  }, []);

  const remove = useCallback(async (id) => {
    if (!window.confirm('Delete this contact message permanently?')) return;
    try {
      await contactAPI.adminDelete(id);
      setMessages((prev) => prev.filter((m) => m.message_id !== id));
      toast.success('Deleted');
    } catch (_e) {
      toast.error('Failed to delete');
    }
  }, []);

  const sendReply = useCallback(async (id) => {
    const body = (replyText || '').trim();
    if (body.length < 5) { toast.error('Reply must be at least 5 characters'); return; }
    setReplySending(true);
    try {
      const r = await contactAPI.adminReply(id, body);
      const sentOk = r?.data?.sent;
      toast.success(sentOk ? 'Reply sent to user via email' : 'Reply saved (email not configured — saved as record)');
      setReplyOpenId(null);
      setReplyText('');
      // Refresh local row state — status flips to resolved
      setMessages((prev) => prev.map((m) => m.message_id === id ? { ...m, status: 'resolved' } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setReplySending(false);
    }
  }, [replyText]);

  return (
    <div className="space-y-3" data-testid="admin-contact-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-[#1800ad]" />
          <h2 className="text-base sm:text-lg font-bold text-[#0A0B10]">Contact Messages</h2>
          <span className="text-xs text-gray-500">({messages.length})</span>
        </div>
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              data-testid={`contact-filter-${f.id || 'all'}`}
              onClick={() => setStatus(f.id)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                status === f.id ? 'bg-[#1800ad] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>
      ) : messages.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500" data-testid="contact-empty">No messages.</div>
      ) : (
        <div className="space-y-2">
          {messages.map((m) => {
            const badge = STATUS_BADGE[m.status] || STATUS_BADGE.new;
            return (
              <div key={m.message_id} data-testid={`contact-row-${m.message_id}`} className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 hover:shadow-sm transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-[#0A0B10] truncate">{m.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      <a href={`mailto:${m.email}`} className="hover:text-[#1800ad]">{m.email}</a>
                      {m.phone ? <> · <a href={`tel:${m.phone}`} className="hover:text-[#1800ad]">{m.phone}</a></> : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                    <span className="text-[10px] text-gray-400">{new Date(m.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                  </div>
                </div>
                <p className="text-xs text-[#4B5563] mb-1"><span className="font-semibold text-[#0A0B10]">Subject:</span> {m.subject || 'General Enquiry'}</p>
                <p className="text-sm text-[#0A0B10] whitespace-pre-wrap break-words mb-3">{m.message}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.status !== 'in_progress' && (
                    <button
                      data-testid={`contact-mark-progress-${m.message_id}`}
                      onClick={() => updateStatus(m.message_id, 'in_progress')}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    ><Clock size={12} /> In Progress</button>
                  )}
                  {m.status !== 'resolved' && (
                    <button
                      data-testid={`contact-mark-resolved-${m.message_id}`}
                      onClick={() => updateStatus(m.message_id, 'resolved')}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                    ><CheckCircle size={12} /> Resolve</button>
                  )}
                  <a
                    href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject || 'GenzoX support'))}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white transition-colors"
                  ><Mail size={12} /> Reply (mailto)</a>
                  <button
                    type="button"
                    data-testid={`contact-reply-open-${m.message_id}`}
                    onClick={() => {
                      setReplyOpenId(replyOpenId === m.message_id ? null : m.message_id);
                      setReplyText('');
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#1800ad] text-white hover:bg-[#0f0080] transition-colors"
                  ><Reply size={12} /> Reply via Admin</button>
                  {m.phone && (
                    <a
                      href={`tel:${m.phone}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white transition-colors"
                    ><Phone size={12} /> Call</a>
                  )}
                  <button
                    data-testid={`contact-delete-${m.message_id}`}
                    onClick={() => remove(m.message_id)}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  ><Trash2 size={12} /> Delete</button>
                </div>

                {replyOpenId === m.message_id && (
                  <div className="mt-3 border-t border-gray-100 pt-3" data-testid={`contact-reply-panel-${m.message_id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#1800ad] mb-1">Compose Reply</p>
                    <p className="text-[11px] text-gray-500 mb-2">
                      Sent from <span className="font-semibold text-[#0A0B10]">noreply@genzox.in</span> ·
                      Replies go to <a href="mailto:help@genzox.in" className="font-semibold text-[#1800ad]">help@genzox.in</a>
                    </p>
                    <textarea
                      data-testid={`contact-reply-text-${m.message_id}`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={4}
                      maxLength={8000}
                      placeholder={`Hi ${m.name?.split(' ')[0] || 'there'}, thanks for getting in touch with GenzoX. …`}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none resize-none"
                    />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-gray-400">{replyText.length}/8000</span>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => { setReplyOpenId(null); setReplyText(''); }}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >Cancel</button>
                        <button
                          type="button"
                          data-testid={`contact-reply-send-${m.message_id}`}
                          onClick={() => sendReply(m.message_id)}
                          disabled={replySending}
                          className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-[#1800ad] text-white hover:bg-[#0f0080] transition-colors disabled:opacity-60"
                        ><Send size={12} /> {replySending ? 'Sending…' : 'Send Reply'}</button>
                      </div>
                    </div>
                  </div>
                )}

                {Array.isArray(m.admin_replies) && m.admin_replies.length > 0 && (
                  <div className="mt-3 border-t border-gray-100 pt-2 space-y-1" data-testid={`contact-replies-${m.message_id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Previous Replies</p>
                    {m.admin_replies.map((rp) => (
                      <div key={rp.reply_id} className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-[#0A0B10]">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-semibold">{rp.by_name || 'Admin'}</span>
                          <span className="text-[10px] text-gray-400">{new Date(rp.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}{rp.sent === false ? ' · email failed' : ''}</span>
                        </div>
                        <p className="whitespace-pre-wrap break-words text-[#4B5563]">{rp.body}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminContactTab;
